import './Header.scss';

import { type FC, useMemo, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router';
import Measure from 'react-measure';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import useFontFamiliesQuery from 'queries/useFontFamiliesQuery';

import * as state from 'state';
import Logo from 'components/vectors/Logo';

import { useThrottledScroll } from 'utils/useThrottledScroll';
import { useReactiveVar } from '@apollo/client';

interface Props {}

const Header: FC<Props> = () => {
  const { t, i18n } = useTranslation();

  const { data } = useFontFamiliesQuery();

  const conceptFonts = useMemo(() => {
    return (
      data?.fontFamilies &&
      data?.fontFamilies.some((family) => family.conceptFont)
    );
  }, [data?.fontFamilies]);

  const [scrolled, setScrolled] = useState(false);

  useThrottledScroll(() => {
    setScrolled(window.scrollY > 0);
  });

  const location = useLocation();

  const floatingHeader = location.pathname === '/';

  const scrolledToConcepts = useReactiveVar(state.scrolledToConcepts);

  return (
    <Measure
      bounds
      margin
      onResize={(contentRect) => {
        state.headerHeight(
          (contentRect.bounds?.height || 0) +
            (contentRect.margin?.top || 0) +
            (contentRect.margin?.bottom || 0),
        );
      }}
    >
      {({ measureRef }) => (
        <header
          className={classNames('Header', {
            'is-floating': floatingHeader,
            'is-scrolled': scrolled,
          })}
          ref={measureRef}
        >
          <div className="Header-content">
            <Link className="Header-logo" to="/">
              <Logo />
            </Link>

            <nav className="Header-navigation">
              <NavLink
                className={({ isActive }) =>
                  classNames('Header-navigation-link', {
                    active: isActive && !scrolledToConcepts,
                  })
                }
                to="/"
              >
                {t('pages.fonts')}
              </NavLink>

              {conceptFonts && (
                <Link
                  className={classNames('Header-navigation-link', {
                    active: location.pathname === '/' && scrolledToConcepts,
                  })}
                  to="/#concepts"
                >
                  {t('pages.concepts')}
                </Link>
              )}

              <NavLink className="Header-navigation-link" to="/about">
                {t('pages.about')}
              </NavLink>

              <NavLink className="Header-navigation-link" to="/faq">
                {t('pages.frequentlyAskedQuestions')}
              </NavLink>

              <NavLink
                className={() =>
                  classNames('Header-navigation-link', {
                    active: /^\/(account|session)/.test(location.pathname),
                  })
                }
                to="/account"
              >
                {t('pages.account')}
              </NavLink>

              <select
                className="Header-navigation-select"
                value={i18n.language}
                data-test-id="language-select"
                onChange={(event) => {
                  i18n.changeLanguage(event.currentTarget.value);
                }}
              >
                <option value="en">EN</option>
                <option value="de">DE</option>
              </select>
            </nav>
          </div>
        </header>
      )}
    </Measure>
  );
};

export default Header;
